import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Wallet, CheckCircle2 } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function Payouts() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/payouts");
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load payouts");
      setData({ reps: [], total_earned: 0, total_paid: 0, total_outstanding: 0 });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (rep) => {
    setBusyId(rep.rep_id);
    try {
      const { data: res } = await api.post(`/admin/payouts/${rep.rep_id}/mark-paid`);
      toast.success(`Marked ${money(rep.outstanding)} paid to ${rep.name}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to mark paid");
    } finally {
      setBusyId(null);
    }
  };

  if (!data) return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;

  return (
    <div className="space-y-8" data-testid="payouts-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-navy text-white flex items-center justify-center">
          <Wallet className="h-6 w-6" />
        </div>
        <div>
          <h1 className="font-display text-3xl text-navy tracking-tight">Payout Tracking</h1>
          <p className="text-muted-foreground text-sm mt-1">Track what each rep has earned and mark overrides as paid out.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-white p-5" data-testid="payouts-earned">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Total earned</p>
          <p className="font-display text-2xl text-navy mt-2">{money(data.total_earned)}</p>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-5" data-testid="payouts-paid">
          <p className="text-xs uppercase tracking-wider text-green-700">Paid out</p>
          <p className="font-display text-2xl text-green-700 mt-2">{money(data.total_paid)}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="payouts-outstanding">
          <p className="text-xs uppercase tracking-wider text-amber-700">Outstanding</p>
          <p className="font-display text-2xl text-amber-700 mt-2">{money(data.total_outstanding)}</p>
        </div>
      </div>

      {data.reps.length === 0 ? (
        <p className="text-muted-foreground" data-testid="payouts-empty">No earnings recorded yet.</p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-navy font-semibold">Representative</TableHead>
                <TableHead className="text-navy font-semibold text-right">Earned</TableHead>
                <TableHead className="text-navy font-semibold text-right">Paid</TableHead>
                <TableHead className="text-navy font-semibold text-right">Outstanding</TableHead>
                <TableHead className="text-navy font-semibold text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.reps.map((r) => (
                <TableRow key={r.rep_id} data-testid={`payout-row-${r.rep_id}`}>
                  <TableCell>
                    <p className="font-medium text-navy">{r.name}</p>
                    <p className="text-xs text-muted-foreground">{r.email} · <span className="font-mono">{r.referral_code}</span></p>
                  </TableCell>
                  <TableCell className="text-right text-navy">{money(r.earned)}</TableCell>
                  <TableCell className="text-right text-green-700">{money(r.paid)}</TableCell>
                  <TableCell className="text-right font-semibold text-amber-700">{money(r.outstanding)}</TableCell>
                  <TableCell className="text-right">
                    {r.outstanding > 0 ? (
                      <Button
                        size="sm"
                        onClick={() => markPaid(r)}
                        disabled={busyId === r.rep_id}
                        data-testid={`mark-paid-${r.rep_id}`}
                        className="rounded-full bg-green-600 hover:bg-green-700 text-white gap-1.5"
                      >
                        {busyId === r.rep_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        Mark paid
                      </Button>
                    ) : (
                      <span className="text-xs text-green-700 font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Settled</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
